import { SpinTransportError } from "@slot/client-engine";
import type {
  GambleCompleteRequest,
  GambleCompleteResponse,
  GambleRequest,
  GambleResponse,
  PresentationCompleteRequest,
  PresentationCompleteResponse,
  RoundProvider,
  SpinRequest,
  SpinResponse,
  SpinResult,
} from "@slot/client-engine";
import { ScriptedRoundProvider } from "./ScriptedRoundProvider";
import type { ScriptedFixtureCatalog } from "./ScriptedFixtureCatalog";
import type { SelectableFixture } from "./SelectableFixture";
import type { FixtureOption, FixturePlaylistPort } from "../dev/FixturePlaylistPort";
import type { RoundFaultPort, RoundFaults, SpinRejectionCode } from "../dev/RoundFaultPort";

type ScriptedOptions = NonNullable<ConstructorParameters<typeof ScriptedRoundProvider>[1]>;

/**
 * The offline server a title runs on without a backend, and the dev tools' handle on it: ONE
 * `ScriptedRoundProvider` over the game's catalog (one round counter, one settlement and gamble record
 * per round), with a playlist of the game's selectable fixtures that replaces the catalog's base round
 * robin until it is cleared (the hook's `loadFixture` is a playlist of one) and one-shot faults on the
 * next requests. Until 2026-10-01 the game's `SelectableFixtureProvider` pinned a fixture by building a
 * second scripted provider, so a round settled after the pin changed reached a server that never served
 * it, and a re-pinned fixture repeated its round id (decision 37).
 */
export class FixtureRoundProvider implements RoundProvider, FixturePlaylistPort, RoundFaultPort {
  private readonly catalog: ScriptedFixtureCatalog;
  private readonly options: ScriptedOptions;
  private readonly server: ScriptedRoundProvider;
  private readonly fixtures: readonly SelectableFixture[];
  private readonly fixturesById: ReadonlyMap<string, SelectableFixture>;
  private playlist: readonly string[] = [];
  private cursor = 0;
  private nextSpinFault: RoundFaults["nextSpin"] = null;
  private settlementFailuresLeft = 0;
  private latencyMs: number;

  public constructor(catalog: ScriptedFixtureCatalog, fixtures: readonly SelectableFixture[], options: ScriptedOptions = {}) {
    this.catalog = catalog;
    this.options = options;
    this.latencyMs = options.simulatedLatencyMs ?? 0;
    this.server = new ScriptedRoundProvider(catalog, options);
    this.fixtures = fixtures;
    this.fixturesById = new Map(fixtures.map((fixture) => [fixture.id, fixture]));
    if (this.fixturesById.size !== fixtures.length) throw new Error("FixtureRoundProvider: two selectable fixtures share an id.");
  }

  public listFixtures(): readonly FixtureOption[] {
    return this.fixtures;
  }

  public getPlaylist(): readonly string[] {
    return this.playlist;
  }

  public getPlaylistCursor(): number {
    return this.cursor;
  }

  public setPlaylist(ids: readonly string[]): void {
    const unknown = ids.filter((id) => !this.fixturesById.has(id));
    if (unknown.length > 0) throw new Error(`Unknown fixture ${unknown.map((id) => `"${id}"`).join(", ")}. Known: ${[...this.fixturesById.keys()].join(", ")}`);
    this.playlist = [...ids];
    this.cursor = 0;
    this.server.setBaseSource(ids.length > 0 ? { next: () => this.nextPlaylistFixture() } : null);
  }

  /**
   * The next spin request comes back refused: the server's `insufficient_funds` by default, or the
   * backend's `player_spin_in_flight` — its 409 while another spin for the player is executing, sent
   * like the backend's with no balance snapshot.
   */
  public rejectNextSpin(code: SpinRejectionCode = "insufficient_funds"): void {
    this.nextSpinFault = code;
  }

  /**
   * The next spin is taken by the "server" and its reply lost on the way back: the round is served
   * (after the simulated latency, like any other) and the client gets a network failure instead —
   * the real-money case where the bet may be spent and the client cannot know. Until 2026-09-26 it
   * threw a plain error before serving, so the game took its generic path and no test ever reached
   * "Connection Lost" (review finding 42).
   */
  public failNextSpin(): void {
    this.nextSpinFault = "lost_reply";
  }

  /**
   * The next `count` settlement requests fail as a lost connection (a network `SpinTransportError`,
   * what `BackendRoundProvider` throws when a request or its reply is lost): the round stays pending,
   * as a lost acknowledgement leaves it. Until 2026-09-29 this was a plain Error, which the game worded
   * as "Something went wrong", copy no live settlement failure shows (review 2026-09-27 #64). A count,
   * not a flag, because one settlement may be retried (decision 17 as amended 2026-09-23): on a winning
   * feature round the panel's early settlement absorbs a single failure and the end-of-replay
   * settlement succeeds, so only `2` reaches the reload modal there.
   */
  public failNextSettlement(count = 1): void {
    this.settlementFailuresLeft = count;
  }

  /**
   * Extra round-trip latency for every following request, playlist or not. Until 2026-09-26 it
   * reached only a pinned fixture, by rebuilding that provider mid-round (review finding 32).
   */
  public setSimulatedLatency(ms: number): void {
    this.latencyMs = Math.max(0, ms);
    this.server.setSimulatedLatency(this.latencyMs);
  }

  public getFaults(): RoundFaults {
    return { nextSpin: this.nextSpinFault, settlementFailures: this.settlementFailuresLeft, latencyMs: this.latencyMs };
  }

  public async requestRoundSnapshot(spinRequest: SpinRequest): Promise<SpinResult> {
    const fault = this.nextSpinFault;
    this.nextSpinFault = null;
    if (fault === "lost_reply") {
      await this.server.requestRoundSnapshot(spinRequest);
      throw new SpinTransportError("network", "FixtureRoundProvider: simulated lost reply — the round was served, the reply never arrived");
    }
    if (fault === "player_spin_in_flight") {
      return {
        schemaVersion: "3",
        serverTimeIsoString: new Date().toISOString(),
        responseMetadata: { statusCode: 409, statusMessage: "player_spin_in_flight", statusData: { detail: "another spin for this player is already in flight" } },
      };
    }
    if (fault === "insufficient_funds") {
      const rejecting = new ScriptedRoundProvider(this.catalog, { ...this.options, simulatedLatencyMs: this.latencyMs, rejectLowBalanceForTesting: true, rejectOnRequestNumbers: [1] });
      return rejecting.requestRoundSnapshot(spinRequest);
    }
    return this.server.requestRoundSnapshot(spinRequest);
  }

  public acknowledgePresentationComplete(request: PresentationCompleteRequest): Promise<PresentationCompleteResponse> {
    if (this.settlementFailuresLeft > 0) {
      this.settlementFailuresLeft -= 1;
      return Promise.reject(new SpinTransportError("network", "FixtureRoundProvider: simulated settlement failure — the acknowledgement was lost on the way"));
    }
    return this.server.acknowledgePresentationComplete(request);
  }

  public requestGamble(request: GambleRequest): Promise<GambleResponse> {
    return this.server.requestGamble(request);
  }

  public acknowledgeGambleComplete(request: GambleCompleteRequest): Promise<GambleCompleteResponse> {
    return this.server.acknowledgeGambleComplete(request);
  }

  /** The base channel while a playlist plays: its next entry, built fresh for each round. */
  private nextPlaylistFixture(): SpinResponse {
    const fixture = this.fixturesById.get(this.playlist[this.cursor]!)!;
    this.cursor = (this.cursor + 1) % this.playlist.length;
    return fixture.create();
  }
}
